import React, { useState } from "react";
import { FaThumbtack, FaTimes } from "react-icons/fa";
import { useRoomMessages } from "../../../../context/RoomMessagesContext";
import "./PinnedMessagesBar.css";

const PinnedMessagesBar = ({ roomId, userRole, onJumpToMessage }) => {
  const { pinnedMessages, unpinMessage } = useRoomMessages();
  const [isExpanded, setIsExpanded] = useState(false);
  const [unpinning, setUnpinning] = useState(null);

  if (!pinnedMessages || pinnedMessages.length === 0) {
    return null;
  }

  // Handle unpin
  const handleUnpin = async (messageId, e) => {
    e.stopPropagation();

    if (!userRole?.isLeader) return;
    if (!window.confirm("Unpin this message?")) return;

    setUnpinning(messageId);
    try {
      await unpinMessage(roomId, messageId);
    } catch (error) { /* ignored */ } finally {
      setUnpinning(null);
    }
  };

  // Handle click on pinned message - jump to it
  const handlePinnedMessageClick = (messageId) => {
    if (onJumpToMessage) {
      onJumpToMessage(messageId);
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Truncate message content
  const truncateContent = (content, maxLength = 100) => {
    if (!content) return "";
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + "...";
  };

  return (
    <div className="pinned-messages-bar">
      {/* Header - Click to expand/collapse */}
      <div className="pinned-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="pinned-header-left">
          <FaThumbtack className="pin-icon" />
          <span className="pinned-count">
            Pinned Messages ({pinnedMessages.length})
          </span>
        </div>
        <span className="pinned-expand-hint">
          {isExpanded ? "Click to collapse" : "Click to expand"}
        </span>
      </div>

      {/* Pinned Messages List */}
      {isExpanded && (
        <div className="pinned-messages-list">
          {pinnedMessages.map((message) => (
            <div
              key={message._id}
              className="pinned-message-item clickable-pinned"
              onClick={() => handlePinnedMessageClick(message._id)}
            >
              <div className="pinned-message-content">
                {/* Sender Info */}
                <div className="pinned-sender">
                  <img
                    src={
                      message.sender?.profilePhotoUrl || "/default-avatar.png"
                    }
                    alt={message.sender?.name || "User"}
                    className="pinned-avatar"
                  />
                  <div className="pinned-info">
                    <span className="pinned-sender-name">
                      {message.sender?.name || "Unknown User"}
                      {message.sender?.isVerified && (
                        <span className="verified-badge">✓</span>
                      )}
                    </span>
                    <span className="pinned-time">
                      {formatTime(message.createdAt)}
                    </span>
                  </div>
                </div>

                {/* Message Text */}
                <p className="pinned-text">
                  {truncateContent(message.content)}
                </p>
              </div>

              {/* Unpin Button (Leader only) */}
              {userRole?.isLeader && (
                <button
                  className="btn-unpin"
                  onClick={(e) => handleUnpin(message._id, e)}
                  disabled={unpinning === message._id}
                  title="Unpin message"
                >
                  {unpinning === message._id ? "..." : <FaTimes />}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PinnedMessagesBar;
